import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  Menu,
  MenuItem,
  Radio,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useAgentEdit } from 'components/@home/@settings/Tabs/Agents/AgentEditContext';
import { semanticTone } from 'components/@home/@settings/Tabs/Agents/agenticThemeHelpers';
import IdentityCard from 'components/@home/@settings/Tabs/Agents/IdentityCard';
import InstructionSaveFooter from 'components/@home/@settings/Tabs/Agents/InstructionSaveFooter';
import RuleGroupCard from 'components/@home/@settings/Tabs/Agents/tagPanels/RuleGroupCard';
import AgentTag from 'services/api/AgentTag';
import DotsHorizontalIcon from 'mdi-react/DotsHorizontalIcon';
import MinusIcon from 'mdi-react/MinusIcon';
import PlusIcon from 'mdi-react/PlusIcon';
import TagOutlineIcon from 'mdi-react/TagOutlineIcon';
import TrashCanOutlineIcon from 'mdi-react/TrashCanOutlineIcon';
import { arrayOf, bool, func, object, string } from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { buildAgenticPath } from 'utils/agenticRoute';
import { attachedSkillsForAgent, unionToolsFromSkills } from 'utils/skillToolsUnion';
import { I18n } from 'utils/i18n';

const INSTRUCTIONS_MAX = 2000;

const emptyDraft = () => ({
  name: '',
  description: '',
  category: 'General',
  type: 'instruction',
  instructions: '',
  rules: [],
});

const sameRules = (a, b) => JSON.stringify(a || []) === JSON.stringify(b || []);

const draftFromTag = tag =>
  tag
    ? {
        name: tag.name || '',
        description: tag.description || '',
        category: tag.category || 'General',
        type: tag.type || 'instruction',
        instructions: tag.instructions || '',
        rules: tag.rules || [],
      }
    : emptyDraft();

const getStyles = theme => {
  const mutedChip = semanticTone(theme, 'default');
  return {
    editorRoot: {
      flex: '1 1 0',
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      backgroundColor: theme.palette.background.paper,
    },
    body: {
      flex: '1 1 0',
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    },
    chrome: {
      flexShrink: 0,
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
      paddingTop: theme.spacing(2),
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
    },
    moreActionsButton: {
      borderColor: theme.palette.divider,
      minWidth: 0,
    },
    categoryPill: {
      height: 22,
      fontSize: theme.typography.pxToRem(11),
      fontWeight: 600,
      letterSpacing: '0.3px',
      backgroundColor: mutedChip.bg,
      color: mutedChip.fg,
      borderRadius: '4px',
      '& .MuiChip-label': { padding: '0 8px' },
    },
    content: {
      flex: '1 1 0',
      minHeight: 0,
      overflowY: 'auto',
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(3),
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2.5),
    },
    field: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(0.75),
    },
    fieldLabel: {
      fontSize: theme.typography.pxToRem(13),
      fontWeight: 600,
      color: theme.palette.text.primary,
    },
    modeRow: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: theme.spacing(2),
    },
    modeCard: {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: '12px',
      padding: theme.spacing(2),
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: theme.spacing(1),
      transition: 'border-color 120ms ease, background-color 120ms ease',
      '&:hover': {
        borderColor: theme.palette.text.disabled,
      },
    },
    modeCardSelected: {
      borderColor: theme.palette.text.primary,
      backgroundColor: theme.palette.background.paper,
    },
    modeTitle: {
      fontSize: theme.typography.pxToRem(14),
      fontWeight: 700,
    },
    modeDesc: {
      fontSize: theme.typography.pxToRem(12.5),
      color: theme.palette.text.secondary,
      marginTop: theme.spacing(0.5),
    },
    rulesSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
    },
    rulesHeader: {
      fontSize: theme.typography.pxToRem(13.5),
      fontWeight: 700,
    },
    instructionsCounter: {
      alignSelf: 'flex-end',
      fontSize: theme.typography.pxToRem(11.5),
      color: theme.palette.text.secondary,
    },
    instructionsHelper: {
      fontSize: theme.typography.pxToRem(12),
      color: theme.palette.text.secondary,
    },
    addGroupBtn: {
      alignSelf: 'stretch',
      textTransform: 'none',
    },
  };
};

export const TagEditorForm = ({
  tag = null,
  agent,
  skills = [],
  tools = [],
  allTags = [],
  onSaved,
  onClose,
  onDetach = null,
  onDeleted = null,
  active = true,
  closeOnSave = false,
}) => {
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const isEdit = Boolean(tag?._id);

  const [draft, setDraft] = useState(emptyDraft);
  const [saving, setSaving] = useState(false);
  const [saveFeedback, setSaveFeedback] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [identityAnchor, setIdentityAnchor] = useState(null);
  const initialRef = useRef(emptyDraft());
  const saveFeedbackTimeoutRef = useRef(null);

  const clearSaveFeedbackLater = useCallback(() => {
    if (saveFeedbackTimeoutRef.current) {
      clearTimeout(saveFeedbackTimeoutRef.current);
    }
    saveFeedbackTimeoutRef.current = setTimeout(() => {
      setSaveFeedback(null);
      saveFeedbackTimeoutRef.current = null;
    }, 3000);
  }, []);

  useEffect(
    () => () => {
      if (saveFeedbackTimeoutRef.current) {
        clearTimeout(saveFeedbackTimeoutRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    if (active) {
      const next = draftFromTag(tag);
      setDraft(next);
      initialRef.current = next;
      setSaveFeedback(null);
    }
  }, [active, tag]);

  const attachedSkills = useMemo(() => attachedSkillsForAgent(agent, skills), [agent, skills]);
  const { tools: availableTools } = useMemo(
    () => unionToolsFromSkills(attachedSkills, tools),
    [attachedSkills, tools],
  );

  const categoryOptions = useMemo(() => {
    const set = new Set();
    set.add('General');
    (allTags || []).forEach(t => {
      if (t.category) set.add(t.category);
    });
    return Array.from(set);
  }, [allTags]);

  const dirty = useMemo(() => {
    const initial = initialRef.current;
    return (
      initial.name !== draft.name ||
      initial.description !== draft.description ||
      initial.category !== draft.category ||
      initial.type !== draft.type ||
      initial.instructions !== draft.instructions ||
      !sameRules(initial.rules, draft.rules)
    );
  }, [draft]);

  const valid = draft.name.trim().length > 0 && draft.instructions.length <= INSTRUCTIONS_MAX;

  useEffect(() => {
    if (dirty && saveFeedback?.severity === 'success') {
      setSaveFeedback(null);
    }
  }, [dirty, saveFeedback?.severity]);

  const update = patch => setDraft(prev => ({ ...prev, ...patch }));

  const updateGroup = (idx, next) => {
    update({ rules: draft.rules.map((g, i) => (i === idx ? next : g)) });
  };

  const removeGroup = idx => {
    update({ rules: draft.rules.filter((_, i) => i !== idx) });
  };

  const addGroup = () => {
    update({ rules: [...draft.rules, { logic: 'and', rules: [] }] });
  };

  const handleSave = async () => {
    if (!valid || saving || !dirty) return;
    setSaving(true);
    setSaveFeedback(null);
    const body = {
      name: draft.name.trim(),
      description: draft.description.trim(),
      category: draft.category || 'General',
      type: draft.type,
      instructions: draft.type === 'instruction' ? draft.instructions : '',
      rules: draft.type === 'rule' ? draft.rules : [],
    };
    try {
      const saved = isEdit ? await AgentTag.update(tag._id, body) : await AgentTag.create(body);
      onSaved(saved, { created: !isEdit });
      initialRef.current = draftFromTag(saved);
      setSaveFeedback({
        severity: 'success',
        message: I18n.t('AgenticChatFlow.ToolSaved'),
      });
      clearSaveFeedbackLater();
      if (closeOnSave) onClose();
    } catch (e) {
      setSaveFeedback({
        severity: 'error',
        message: e?.response?.data?.error?.message || I18n.t('AgenticChatFlow.TagsSaveError'),
      });
      clearSaveFeedbackLater();
    } finally {
      setSaving(false);
    }
  };

  const handleDetach = () => {
    setMenuAnchor(null);
    if (!tag?._id || !onDetach) return;
    onDetach(tag._id);
    onClose();
  };

  const handleDelete = async () => {
    setMenuAnchor(null);
    if (!tag?._id) return;

    if (!window.confirm(I18n.t('AgenticChatFlow.TagsDeleteConfirm', { name: tag.name }))) return;
    try {
      await AgentTag.destroy(tag._id);
      if (onDeleted) onDeleted(tag._id);
      onClose();
    } catch (e) {
      setSaveFeedback({
        severity: 'error',
        message: e?.response?.data?.error?.message || I18n.t('AgenticChatFlow.TagsDeleteError'),
      });
      clearSaveFeedbackLater();
    }
  };

  const displayTitle =
    draft.name.trim() ||
    (isEdit ? I18n.t('AgenticChatFlow.TagsEditTitle') : I18n.t('AgenticChatFlow.TagsNewTitle'));

  return (
    <Box sx={styles.editorRoot}>
      <Box sx={styles.body}>
        <Box sx={styles.chrome}>
          <IdentityCard
            icon={<TagOutlineIcon size={22} />}
            title={displayTitle}
            description={draft.description.trim() || null}
            emptyDescription={I18n.t('AgenticChatFlow.NoDescription')}
            pill={
              draft.category ? (
                <Chip size="small" sx={styles.categoryPill} label={draft.category} />
              ) : null
            }
            divider
            edit={{
              open: Boolean(identityAnchor),
              anchorEl: identityAnchor,
              onOpen: e => setIdentityAnchor(e.currentTarget),
              onClose: () => setIdentityAnchor(null),
              content: (
                <>
                  <TextField
                    value={draft.name}
                    onChange={e => update({ name: e.target.value })}
                    size="small"
                    fullWidth
                    autoFocus
                    label={I18n.t('AgenticChatFlow.TagsFieldName')}
                  />
                  <TextField
                    value={draft.description}
                    onChange={e => update({ description: e.target.value })}
                    size="small"
                    fullWidth
                    multiline
                    minRows={3}
                    label={I18n.t('AgenticChatFlow.TagsFieldDescription')}
                    placeholder={I18n.t('AgenticChatFlow.TagsFieldDescriptionPlaceholder')}
                  />
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      justifyContent: 'flex-end',
                    }}
                  >
                    <Button onClick={() => setIdentityAnchor(null)} color="inherit">
                      {I18n.t('AgenticChatFlow.Cancel')}
                    </Button>
                    <Button
                      onClick={() => setIdentityAnchor(null)}
                      variant="contained"
                      color="primary"
                      disableElevation
                      disabled={!draft.name.trim()}
                    >
                      {I18n.t('AgenticChatFlow.Save')}
                    </Button>
                  </Stack>
                </>
              ),
            }}
            actions={
              isEdit ? (
                <>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={e => setMenuAnchor(e.currentTarget)}
                    size="small"
                    sx={styles.moreActionsButton}
                  >
                    <DotsHorizontalIcon size={20} />
                  </Button>
                  <Menu
                    anchorEl={menuAnchor}
                    open={Boolean(menuAnchor)}
                    onClose={() => setMenuAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  >
                    {onDetach && (
                      <MenuItem onClick={handleDetach}>
                        <MinusIcon size={16} style={{ marginRight: 8 }} />
                        {I18n.t('AgenticChatFlow.Detach')}
                      </MenuItem>
                    )}
                    {onDetach && <Divider />}
                    <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
                      <TrashCanOutlineIcon size={16} style={{ marginRight: 8 }} />
                      {I18n.t('AgenticChatFlow.Delete')}
                    </MenuItem>
                  </Menu>
                </>
              ) : null
            }
          />
        </Box>
        <Box sx={styles.content}>
          <Box sx={styles.field}>
            <Typography sx={styles.fieldLabel}>
              {I18n.t('AgenticChatFlow.TagsFieldCategory')}
            </Typography>
            <Autocomplete
              size="small"
              freeSolo
              options={categoryOptions}
              value={draft.category || 'General'}
              onChange={(_, v) => update({ category: v || 'General' })}
              onInputChange={(_, v) => update({ category: v || 'General' })}
              renderInput={params => <TextField {...params} />}
            />
          </Box>
          <Box sx={styles.modeRow}>
            <Box
              sx={[styles.modeCard, draft.type === 'instruction' && styles.modeCardSelected]}
              onClick={() => update({ type: 'instruction' })}
              role="button"
              tabIndex={0}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  update({ type: 'instruction' });
                }
              }}
            >
              <Box>
                <Typography sx={styles.modeTitle}>
                  {I18n.t('AgenticChatFlow.TagsInstructionMode')}
                </Typography>
                <Typography sx={styles.modeDesc}>
                  {I18n.t('AgenticChatFlow.TagsInstructionModeHelper')}
                </Typography>
              </Box>
              <Radio checked={draft.type === 'instruction'} size="small" />
            </Box>
            <Box
              sx={[styles.modeCard, draft.type === 'rule' && styles.modeCardSelected]}
              onClick={() => update({ type: 'rule' })}
              role="button"
              tabIndex={0}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  update({ type: 'rule' });
                }
              }}
            >
              <Box>
                <Typography sx={styles.modeTitle}>
                  {I18n.t('AgenticChatFlow.TagsRuleMode')}
                </Typography>
                <Typography sx={styles.modeDesc}>
                  {I18n.t('AgenticChatFlow.TagsRuleModeHelper')}
                </Typography>
              </Box>
              <Radio checked={draft.type === 'rule'} size="small" />
            </Box>
          </Box>
          {draft.type === 'instruction' && (
            <Box sx={styles.field}>
              <Typography sx={styles.fieldLabel}>
                {I18n.t('AgenticChatFlow.TagsInstructionsLabel')}
              </Typography>
              <TextField
                multiline
                minRows={5}
                maxRows={14}
                fullWidth
                value={draft.instructions}
                onChange={e => update({ instructions: e.target.value.slice(0, INSTRUCTIONS_MAX) })}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={styles.instructionsHelper}>
                  {I18n.t('AgenticChatFlow.TagsInstructionsHelper')}
                </Typography>
                <Typography sx={styles.instructionsCounter}>
                  {`${draft.instructions.length}/${INSTRUCTIONS_MAX}`}
                </Typography>
              </Box>
            </Box>
          )}
          {draft.type === 'rule' && (
            <Box sx={styles.rulesSection}>
              <Typography sx={styles.rulesHeader}>
                {I18n.t('AgenticChatFlow.TagsRulesHeader')}
              </Typography>
              {draft.rules.map((group, idx) => (
                <RuleGroupCard
                  key={idx}
                  group={group}
                  index={idx}
                  availableTools={availableTools}
                  availableSkills={attachedSkills}
                  onChange={next => updateGroup(idx, next)}
                  onRemove={() => removeGroup(idx)}
                />
              ))}
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<PlusIcon size={16} />}
                sx={styles.addGroupBtn}
                onClick={addGroup}
              >
                {I18n.t('AgenticChatFlow.TagsAddRuleGroup')}
              </Button>
            </Box>
          )}
        </Box>
      </Box>
      <InstructionSaveFooter
        dirty={dirty}
        saving={saving}
        saveFeedback={saveFeedback}
        onSave={handleSave}
        saveDisabled={!valid}
      />
    </Box>
  );
};

TagEditorForm.propTypes = {
  tag: object,
  agent: object.isRequired,
  skills: arrayOf(object),
  tools: arrayOf(object),
  allTags: arrayOf(object),
  onSaved: func.isRequired,
  onClose: func.isRequired,
  onDetach: func,
  onDeleted: func,
  active: bool,
  closeOnSave: bool,
};

const TagEditor = ({ tagId }) => {
  const navigate = useNavigate();
  const { flow, authCompany, agent, skills, tools, tags, onSaveAgent, onReloadTags } =
    useAgentEdit();

  const isNew = tagId === 'new';
  const tag = useMemo(() => {
    if (isNew) return null;
    return (tags || []).find(t => String(t._id) === String(tagId)) || null;
  }, [isNew, tagId, tags]);

  const attachedIds = useMemo(() => (agent?.tagIds || []).map(String), [agent?.tagIds]);

  const goBack = () => {
    if (!flow?._id) return;
    navigate(buildAgenticPath(authCompany, { flowId: flow._id, section: 'tags' }));
  };

  const handleSaved = async (saved, { created }) => {
    if (created) {
      const nextIds = Array.from(new Set([...attachedIds, String(saved._id)]));
      onSaveAgent({ tagIds: nextIds });
      await onReloadTags?.();
      navigate(
        buildAgenticPath(authCompany, {
          flowId: flow._id,
          section: 'tags',
          tagId: saved._id,
        }),
      );
      return;
    }
    await onReloadTags?.();
  };

  const handleDetach = id => {
    onSaveAgent({ tagIds: attachedIds.filter(tid => tid !== String(id)) });
  };

  const handleDeleted = id => {
    if (attachedIds.includes(String(id))) {
      onSaveAgent({ tagIds: attachedIds.filter(tid => tid !== String(id)) });
    }
    onReloadTags?.();
    goBack();
  };

  const handleClose = () => {
    goBack();
  };

  if (!isNew && !tag) return null;

  return (
    <TagEditorForm
      tag={tag}
      agent={agent}
      skills={skills}
      tools={tools}
      allTags={tags}
      onSaved={handleSaved}
      onClose={handleClose}
      onDetach={!isNew ? handleDetach : null}
      onDeleted={!isNew ? handleDeleted : null}
    />
  );
};

TagEditor.propTypes = {
  tagId: string.isRequired,
};

export default TagEditor;
